<template>
  <div class="tcm-panel">
    <div v-if="loaded">
      <validation-observer ref="refFormObserver">
        <div class="tcm-header">
          <div class="tcm-header-icon">
            <i class="fas fa-layer-group"></i>
          </div>
          <div>
            <div class="tcm-header-title">{{ msg('Generate contract') }}</div>
            <div class="tcm-header-sub">Multi-property reservation</div>
          </div>
        </div>

        <!-- Toggles Row -->
        <div class="tcm-toggles-card">
          <div class="tcm-toggle-item">
            <label class="tcm-label tcm-label-orange">{{ msg('Export contract to channels') }}</label>
            <validation-provider #default="{ errors }" :name="msg('Hostaway')" rules="required">
              <b-form-checkbox disabled v-model="contract.hostawayApi" name="check-button" switch>
                {{ contract.hostawayApi ? msg('Yes') : msg('No') }}
              </b-form-checkbox>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </div>
          <div class="tcm-toggle-item">
            <label class="tcm-label">{{ msg('newclients') }}</label>
            <validation-provider #default="{ errors }" :name="msg('Client')" rules="required">
              <b-form-checkbox v-model="contract.isNewClient" switch></b-form-checkbox>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </div>
          <div class="tcm-toggle-item">
            <label class="tcm-label">{{ msg('Pricelabs price') }}</label>
            <validation-provider #default="{ errors }" :name="msg('pricelabs')" rules="required">
              <b-form-checkbox v-model="isPricelabs" switch>
                {{ isPricelabs ? msg('Yes') : msg('No') }}
              </b-form-checkbox>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </div>
          <div class="tcm-toggle-item">
            <label class="tcm-label">{{ msg('Paid') }}</label>
            <b-form-checkbox v-model="contract.paid" switch>
              {{ contract.paid ? msg('Yes') : msg('No') }}
            </b-form-checkbox>
          </div>
          <div class="tcm-toggle-item">
            <label class="tcm-label">{{ msg('Over 30 days') }}</label>
            <b-form-checkbox v-model="contract.over30Days" switch>
              {{ contract.over30Days ? msg('Yes') : msg('No') }}
            </b-form-checkbox>
          </div>
        </div>

        <!-- Over 30 days Platform Selection -->
        <div v-if="contract.over30Days" class="tcm-platform-card">
          <b-form-radio-group v-model="contract.platform30DaysId" name="extendedStayPlatforms" class="tcm-platform-grid">
            <b-form-radio v-for="option in options" :key="option.value" :value="option.value" button button-variant="outline-primary" class="tcm-platform-option">
              <div class="d-flex flex-column align-items-center">
                <img :src="option.image" alt="Option Image" class="tcm-platform-img mb-1">
                <span>{{ option.text }}</span>
              </div>
            </b-form-radio>
          </b-form-radio-group>
        </div>

        <!-- Client Section -->
        <div class="tcm-section-label"><i class="fas fa-user mr-50"></i> {{ msg('Client') }}</div>
        <b-row class="tcm-form-grid">
          <template v-if="!contract.isNewClient">
            <b-col cols="12" md="12" lg="6">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('Client Id') }}</label>
                <validation-provider #default="{ errors }" :name="msg('Client')" rules="required">
                  <client-picker v-model="contract.clientId"/>
                  <small class="text-danger">{{ msg(errors[0]) }}</small>
                </validation-provider>
              </div>
            </b-col>
          </template>
          <template v-else>
            <b-col cols="12" md="6" lg="3">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('Firstname') }}</label>
                <validation-provider #default="{ errors }" :name="msg('Client')" rules="required">
                  <b-form-input v-model="contract.client.firstname" class="tcm-input" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
            <b-col cols="12" md="6" lg="3">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('Lastname') }}</label>
                <validation-provider #default="{ errors }" :name="msg('Lastname')" rules="required">
                  <b-form-input v-model="contract.client.lastname" class="tcm-input" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
            <b-col cols="12" md="6" lg="3">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('Phone') }}</label>
                <validation-provider #default="{ errors }" :name="msg('Phone')" rules="required">
                  <b-form-input v-model="contract.client.phone" class="tcm-input" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
            <b-col cols="12" md="6" lg="3">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('Email') }}</label>
                <validation-provider #default="{ errors }" :name="msg('Email')" rules="required">
                  <b-form-input v-model="contract.client.email" class="tcm-input" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
            <b-col cols="12" md="6" lg="3">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('Address') }}</label>
                <validation-provider #default="{ errors }" :name="msg('Address')" rules="required">
                  <b-form-input v-model="contract.client.address.street" class="tcm-input" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
            <b-col cols="12" md="6" lg="2">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('Zip') }}</label>
                <validation-provider #default="{ errors }" :name="msg('Zip')" rules="required">
                  <b-form-input v-model="contract.client.address.zip" class="tcm-input" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
            <b-col cols="12" md="6" lg="2">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('City') }}</label>
                <validation-provider #default="{ errors }" :name="msg('City')" rules="required">
                  <b-form-input v-model="contract.client.address.city" class="tcm-input" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
            <b-col cols="12" md="6" lg="3">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('Country') }}</label>
                <validation-provider #default="{ errors }" :name="msg('Country')" rules="required">
                  <v-select v-bind:dir="isRTL ? 'rtl' : 'ltr'" v-model="contract.client.address.country" :options="allCountries" class="w-100 tcm-select" label="name" :reduce="(l) => l.iso" autocomplete="on" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
            <b-col cols="12" md="6" lg="2">
              <div class="tcm-field">
                <label class="tcm-label">{{ msg('Number of guests') }}</label>
                <validation-provider #default="{ errors }" :name="msg('Number of guests')" rules="required">
                  <b-form-input type="number" v-model="contract.numberOfGuests" class="tcm-input" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
          </template>
        </b-row>

        <!-- Properties & Dates -->
        <div class="tcm-section-label mt-2"><i class="fas fa-building mr-50"></i> {{ msg('Properties & Dates') }}</div>
        <b-row class="tcm-form-grid">
          <b-col cols="12">
            <div class="tcm-field">
              <label class="tcm-label">{{ msg('Real estates') }}</label>
              <real-estate-picker multiple v-model="contract.propertyIds"/>
            </div>
          </b-col>
          <b-col cols="12" md="4">
            <div class="tcm-field">
              <label class="tcm-label">{{ msg('Start') }}</label>
              <validation-provider #default="{ errors }" :name="msg('Start date')" rules="required">
                <calendar-picker v-model="contract.bnbStyleRent.start"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </div>
          </b-col>
          <b-col cols="12" md="4">
            <div class="tcm-field">
              <label class="tcm-label">{{ msg('End') }}</label>
              <validation-provider #default="{ errors }" :name="msg('End date')" rules="required">
                <calendar-picker v-model="contract.bnbStyleRent.end"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </div>
          </b-col>
          <b-col cols="12" md="4" class="d-flex align-items-end">
            <b-button :disabled="!isPricelabs || contract.propertyIds.length<1 || this.contract.bnbStyleRent.start == null || this.contract.bnbStyleRent.end == null" variant="danger" class="tcm-btn w-100 mb-1" @click="calculatePricelabsPrice">
              <i class="fas fa-calculator mr-50"></i> {{ msg('Calculate Pricelabs price') }}
            </b-button>
          </b-col>
          <b-col cols="12">
            <div class="tcm-field">
              <label class="tcm-label">{{ msg("Cancellation options") }}</label>
              <v-select v-model="cancelationOptionPicked" :options="cancellationOptions" label="name" :clearable="false" :placeholder="msg('Select a cancellation option')" :reduce="o => o.id" class="tcm-select" />
            </div>
          </b-col>
        </b-row>

        <!-- Prices -->
        <div v-if="contract.propertyIds!=null && contract.propertyIds.length>0" class="mt-2">
          <div class="tcm-section-label"><i class="fas fa-euro-sign mr-50"></i> {{ msg('Pricing') }}</div>
          <b-row class="tcm-form-grid">
            <template v-if="isPricelabs">
              <b-col cols="12" md="6" lg="3" v-for="(address, id) in realEstatesIdAddressMap" :key="id">
                <div class="tcm-field">
                  <label class="tcm-label">{{ msg('Price for') }} {{ address }}</label>
                  <validation-provider #default="{ errors }" :name="`${msg('Price for')} ${address}`" rules="required">
                    <b-form-input readonly :id="`realEstate-sell-price-${id}`" type="number" v-model="contract.prices[id]" class="tcm-input" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </div>
              </b-col>
            </template>
            <template v-else>
              <b-col cols="12" md="6" lg="3" v-for="(address, id) in realEstatesIdAddressMap" :key="id">
                <div class="tcm-field">
                  <label class="tcm-label">{{ msg('Price for') }} {{ address }}</label>
                  <validation-provider #default="{ errors }" :name="`${msg('Price for')} ${address}`" rules="required">
                    <b-form-input :id="`realEstate-sell-price-${id}`" type="number" step="any" v-model="contract.prices[id]" class="tcm-input" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </div>
              </b-col>
            </template>
          </b-row>
        </div>
      </validation-observer>
    </div>
    <div v-else-if="this.error != null">
      <div class="tcm-error">
        <i class="fas fa-exclamation-triangle mr-75"></i>
        <strong>{{ error }}</strong>
      </div>
    </div>
    <div v-else class="tcm-loading">
      <b-spinner variant="primary" />
      <span class="ml-75">{{ msg('loading') }}</span>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import ClientPicker from "@/components/components/picker/clientPicker";
import RealEstatePicker from "@/components/components/picker/realEstatesPicker";

export default {
  name: 'treasureContractRentPanelMulti',
  components: {RealEstatePicker, ClientPicker},
  data() {
    return {
      loaded: false,
      pricelabPrices: [],
      error: null,
      selectedOption: null,
      firstProperty: null,
      options: [
        {
          value: "Housing anywhere",
          text: "Housing anywhere",
          image: require('@/assets/images/platforms/housing.jpg'),
        },
        {
          value: "Homelike",
          text: "Homelike",
          image: require('@/assets/images/platforms/homelike.jpg'),
        },
        {
          value: "Wunderflats",
          text: "Wunderflats",
          image: require('@/assets/images/platforms/wunder.jpg'),
        },
        {
          value: "Treasure home",
          text: "Treasure home",
          image: "https://treasurehome.at/uploads/images/tLogo.png",
        }
      ],
      contractId: 0,
      isPricelabs: false,
      cancelationOptionPicked: null,
      cancellationOptions: [],
      contract: {
        prices: {},
        propertyIds: [],
        hostawayApi: true,
        isNewClient: true,
        name: '',
        client: {
          firstname: null,
          email: null,
          phone: null,
          lastname: null,
          address: {
            street: null,
            zip: null,
            city: null,
            country: null
          },
        },
        bnbStyleRent: {
          platform: null,
          active: true,
          start: null,
          end: null,
          totalPayment: 0.0,
          oneTimePayment: false,
          totalPaymentDiscount: null,
          totalServiceFee: null,
          totalPriceHandlingFee: null,
        },
        type: null,
      },
      languageOptions: [
        {id: 'EN', name: 'English'},
        {id: 'ES', name: 'Spanish'},
        {id: 'FR', name: 'French'},
        {id: 'DE', name: 'German'},
        {id: 'IT', name: 'Italian'},
        {id: 'PT', name: 'Portuguese'},
        {id: 'RU', name: 'Russian'},
        {id: 'CN', name: 'Chinese'},
        {id: 'JP', name: 'Japanese'}
      ],
      Refilter: {
        like: {search: '', name: "", shortDescription: ""},
        eq: {type: "", realEstateArea: {}, featureStatus: null},
        lte: {realEstatePrices: {}},
        gte: {realEstatePrices: {}},
        in: {}
      },
      realEstatesIdAddressMap: {},
      listeners: {
        submit: () => {
        }
      }
    }
  },
  watch: {
    id: function () {
      this.onLoad()
      this.error = null
    },
    'contract.propertyIds': {
      handler(newPropertyIds, oldPropertyIds) {
        console.log('propertyIds changed', newPropertyIds, oldPropertyIds);
        this.getProperties()
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters('realEstate', ['getAllTypes']),
    ...mapGetters('data', ['getAllCountries', 'getAllLanguage']),
    allCountries() {
      return Object.values(this.getAllCountries)
    },
    propertiesWithZeros() {
      if (this.contract && Array.isArray(this.contract.propertyIds) && this.contract.propertyIds.length > 0) {
        return this.contract.propertyIds.map(() => ({value: 0}));
      }
      return [];
    },
  },
  created() {
    this.onLoad()
    this.$root.$on('crm::submitContract', (this.listeners.submit = () => {
      this.submit()
    }))
    this.$root.$on('crm::checkContract', (this.listeners.checkContract = () => {
      this.checkContract()
    }))
  },
  beforeDestroy() {
    this.$root.$off('crm::submitContract', this.listeners.submit)
    this.$root.$off('crm::checkContract', this.listeners.checkContract)
  },
  methods: {
    ...mapActions('realEstateContracts', {
      _createContractMulti: 'createContractMulti', _checkCreateMulti: 'checkCreateMulti'
    }),
    ...mapActions('realEstate', ['getAllRealEstates']),
    ...mapActions('pricelabs', ['calcPrices']),
    setData(propertyIds, start, end) {
      this.contract.propertyIds = propertyIds
      this.contract.bnbStyleRent.start = start
      this.contract.bnbStyleRent.end = end
      this.loaded = true
    },
    getProperties() {
      this.Refilter.in = {id: this.contract.propertyIds}
      this.getAllRealEstates({
        page: 1,
        amount: 9999,
        filter: this.Refilter,
        sortcolumn: 'updatedDate',
        sortby: 'desc',
      }).then(this.onRERetrieve, (error) => this.error = error)
    },
    calculatePricelabsPrice() {
      let $this = this;
      let request = {
        realEstateIds: this.contract.propertyIds,
        start: this.contract.bnbStyleRent.start,
        end: this.contract.bnbStyleRent.end,
      }
      this.calcPrices({body: request}).then((ans) => {
        $this.contract.prices = ans
      }, (error) => this.error = error)
    },
    onRERetrieve(data) {
      this.firstProperty = data["RealEstates"][0]
      this.realEstatesIdAddressMap = data["RealEstates"].reduce((acc, cur) => {
        acc[cur.id] = cur.address + " " + cur.house + "/" + cur.apt + " " + cur.zip
        return acc
      }, {});
      this.loadCancellationOptions();
    },
    onLoad() {
      this.loaded = true
    },
    loadCancellationOptions() {
      if (this.firstProperty == null) {
        return;
      }
      if (this.firstProperty.realEstateCancel == null) {
        this.cancellationOptions = [
          {id: 'nonRefundable', name: this.msg('cancellationOptions.nonRefundable')}
        ];
        return;
      }
      this.cancellationOptions = [
        {id: 'nonRefundable', name: this.msg('cancellationOptions.nonRefundable')}
      ];
      if (this.firstProperty.realEstateCancel.threeDays) {
        this.cancellationOptions.push({
          id: 'threeDays',
          name: `${this.msg('cancellationOptions.cancelUpTo')} 3 ${this.msg('cancellationOptions.days')} (${this.msg('cancellationOptions.extraFee')}: ${this.firstProperty.realEstateCancel.threeDaysFeePercent}%)`
        });
      }
      if (this.firstProperty.realEstateCancel.sevenDays) {
        this.cancellationOptions.push({
          id: 'sevenDays',
          name: `${this.msg('cancellationOptions.cancelUpTo')} 7 ${this.msg('cancellationOptions.days')} (${this.msg('cancellationOptions.extraFee')}: ${this.firstProperty.realEstateCancel.sevenDaysFeePercent}%)`
        });
      }
      if (this.firstProperty.realEstateCancel.fourteenDays) {
        this.cancellationOptions.push({
          id: 'fourteenDays',
          name: `${this.msg('cancellationOptions.cancelUpTo')} 14 ${this.msg('cancellationOptions.days')} (${this.msg('cancellationOptions.extraFee')}: ${this.firstProperty.realEstateCancel.fourteenDaysFeePercent}%)`
        });
      }
    },
    createContract() {
      let $this = this
      if (this.contract.paid == null)
        this.contract.paid = false
      if (this.contract.over30Days == null)
        this.contract.over30Days = false
      else if (this.contract.over30Days && this.contract.platform30DaysId == null) {
        this.contract.platform30DaysId = "Treasure home"
      }
      this.contract.cancelationOptionPicked = this.cancelationOptionPicked;
      this.$refs['refFormObserver'].validate().then(valid => {
        if (!valid)
          return;
        $this._createContractMulti(this.contract)
            .then(
                (id) => {
                  $this.contractId = id
                  $this.$emit('submitted', {
                    status: true,
                    server: id,
                    id
                  })
                },
                (a) => $this.$emit('submitted', {
                  status: false,
                  server: a
                })
            )
            .finally(() => {
              if ($this.contractId != null && $this.contractId !== 0 && $this.contractId !== 'success')
                this.$router.push(`/real_estate/contracts/rent/${$this.contractId}`);
            })
      })
    },
    validate(field) {
      return this.$v.contract[field] != null && this.$v.contract[field].$error === true
    },
    submit() {
      return this.createContract()
    },
    checkContract() {
      let $this = this;
      this.$refs['refFormObserver'].validate().then(valid => {
        if (!valid)
          return;
        $this._checkCreateMulti(this.contract)
            .then(
                (id) => {
                  $this.contractId = id
                  $this.$emit('checked', {
                    status: true,
                    server: id,
                    id
                  })
                },
                (a) => $this.$emit('checked', {
                  status: false,
                  server: a
                })
            )
      })
    }
  }
}
</script>

<style scoped>
.tcm-panel { padding: 8px; }
.tcm-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.tcm-header-icon { width: 44px; height: 44px; border-radius: 14px; display: flex; align-items: center; justify-content: center; background: rgba(115,103,240,.12); color: #7367f0; font-size: 18px; flex-shrink: 0; }
.tcm-header-title { font-weight: 900; font-size: 16px; }
.tcm-header-sub { font-size: 12px; opacity: .6; }
.tcm-toggles-card { display: flex; gap: 20px; padding: 14px 18px; border-radius: 14px; background: rgba(0,0,0,.02); border: 1px solid rgba(0,0,0,.06); margin-bottom: 20px; flex-wrap: wrap; }
.tcm-toggle-item { min-width: 120px; }
.tcm-platform-card { padding: 16px; border-radius: 14px; background: rgba(0,0,0,.01); border: 1px solid rgba(0,0,0,.06); margin-bottom: 20px; }
.tcm-platform-grid { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 12px; }
.tcm-platform-option { text-align: center; }
.tcm-platform-img { height: 80px; object-fit: cover; border-radius: 10px; }
.tcm-section-label { font-weight: 800; font-size: 12px; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 14px; color: #7367f0; display: flex; align-items: center; }
.tcm-form-grid { row-gap: 4px; }
.tcm-field { margin-bottom: 8px; }
.tcm-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; opacity: .65; margin-bottom: 4px; display: block; }
.tcm-label-orange { color: #ff9f43; opacity: 1; }
.tcm-input { border-radius: 10px; }
.tcm-select :deep(.vs__dropdown-toggle) { border-radius: 10px; }
.tcm-btn { border-radius: 12px; font-weight: 700; }
.tcm-error { padding: 16px; border-radius: 14px; background: rgba(234,84,85,.08); border: 1px solid rgba(234,84,85,.2); color: #ea5455; }
.tcm-loading { display: flex; align-items: center; justify-content: center; min-height: 200px; opacity: .7; }
</style>
